import { CascadiaTextProps, Link, Text } from "@certn/cascadia";
import { Card, Icon } from "@certn/cascadia-react";
import { parseInfoParagraphBody } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

type InfoSectionProps = {
  infoArray: string[][];
  title: string;
};

export function InfoSection({ infoArray, title }: InfoSectionProps) {
  if (!infoArray.length) return null;

  return (
    <div className={styles.sectionContainer}>
      <Text style="global/heading-large">{title}</Text>
      {infoArray.map((item: string[], index) => {
        return (
          <div key={index} className={styles.sectionItemContainer}>
            <div className={styles.sectionItem}>
              <Icon name="check" color="text-accent-tofino" />
            </div>
            <Text style="global/heading-medium" color="text-weak">
              {item[0] + ": "}
              <Text style="form/body-primary" color="text-weak">
                {item[1]}
              </Text>
            </Text>
            <br />
          </div>
        );
      })}
    </div>
  );
}

type CommonFieldsProps = {
  description?: string | null;
  infoParagraphs?: string[][];
  estimatedTurnaroundTime?: string | null;
  disclaimersIconList?: string[];
  disclaimers?: string[];
  coverage?: string;
  keyFeatures?: string[][];
  howItCanHelpYou?: string[][];
};

function InfoParagraphBody({
  subtitle,
  body,
}: {
  subtitle: string;
  body: string | undefined;
}) {
  const { intro, listItems } = parseInfoParagraphBody(body);

  return (
    <>
      {subtitle ? <Text style="global/heading-medium">{subtitle}</Text> : null}
      {intro ? (
        <Text
          style="form/body-primary"
          className={listItems.length ? undefined : styles.preLine}
        >
          {intro}
        </Text>
      ) : null}
      {listItems.map((line) => (
        <div key={line} className={styles.sectionItemContainer}>
          <div className={styles.sectionItem}>
            <Icon name="check" color="text-accent-tofino" />
          </div>
          <Text style="form/body-primary">{line}</Text>
        </div>
      ))}
    </>
  );
}

function InfoParagraphs({
  infoParagraphs,
}: {
  infoParagraphs: CommonFieldsProps["infoParagraphs"];
}) {
  const { t } = useTranslation();

  return (
    <>
      {infoParagraphs?.map(([title, subtitle, body]) => (
        <div
          key={title}
          aria-label={t("{title} section", { title })}
          className={styles.sectionContainer}
        >
          <Text style="global/heading-large" marginTop="space-4">
            {title}
          </Text>
          <br />
          <div style={{ marginTop: "var(--space-1)" }}>
            <InfoParagraphBody subtitle={subtitle} body={body} />
          </div>
        </div>
      ))}
    </>
  );
}

function Disclaimer({
  text,
  textStyle,
}: {
  text: string;
  textStyle: CascadiaTextProps["style"];
}) {
  return (
    <Markdown
      allowedElements={["p", "strong", "em", "a"]}
      components={{
        p: ({ children }) => (
          <Text style={textStyle} color="text-weak">
            {children}
          </Text>
        ),
        a: ({ href, children }) => (
          <Link href={href} testID="">
            {children}
          </Link>
        ),
      }}
    >
      {text}
    </Markdown>
  );
}

export function CommonFields({
  description,
  infoParagraphs,
  estimatedTurnaroundTime,
  disclaimersIconList,
  disclaimers,
  coverage,
  keyFeatures,
  howItCanHelpYou,
}: CommonFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      {coverage && estimatedTurnaroundTime && (
        <Card
          border="none"
          type="default"
          background="background-accent-neutral-weak"
          className={styles.cardContainer}
        >
          <div
            className={styles.cardTextContainer}
            aria-label={t("Turnaround time section")}
          >
            <Text color="text-base" style="global/label-small">
              {t("Turnaround time")}
            </Text>
            <br />
            <Text color="text-accent-tofino" style="global/metric-small">
              {estimatedTurnaroundTime}
            </Text>
          </div>
          <div
            className={styles.cardTextContainer}
            aria-label={t("Coverage Section")}
          >
            <Text color="text-base" style="global/label-small">
              {t("Coverage")}
            </Text>
            <br />
            <Text color="text-accent-tofino" style="global/metric-small">
              {coverage}
            </Text>
          </div>
        </Card>
      )}
      {coverage && estimatedTurnaroundTime && description && (
        <div
          aria-label={t("What is it section")}
          className={styles.sectionContainer}
        >
          <Text style="global/heading-large" marginTop="space-4">
            {t("What is it?")}
          </Text>
          <br />
          <div style={{ marginTop: "var(--space-1)" }}>
            <Text style="form/body-primary" className={styles.preLine}>
              {description}
            </Text>
          </div>
        </div>
      )}
      <InfoParagraphs infoParagraphs={infoParagraphs} />
      <InfoSection
        infoArray={keyFeatures || []}
        title={
          coverage && estimatedTurnaroundTime
            ? t("Additional key features")
            : t("Key features")
        }
      />
      <InfoSection
        infoArray={howItCanHelpYou || []}
        title={t("How it can help you")}
      />

      {Boolean(disclaimersIconList?.length) && (
        <div className={styles.sectionContainer}>
          <Text style="global/heading-large">{t("Disclaimers")}</Text>
          {disclaimersIconList?.map((text, index) => (
            <div key={index} className={styles.sectionItemContainer}>
              <div className={styles.sectionItem}>
                <Icon name="check" color="text-accent-tofino" />
              </div>
              <Disclaimer text={text} textStyle="form/body-primary" />
              <br />
            </div>
          ))}
        </div>
      )}
      {disclaimers?.map((text, index) => (
        <div className={styles.disclaimer} key={index}>
          <Disclaimer text={text} textStyle="form/body-secondary" />
        </div>
      ))}
    </>
  );
}

const styles = {
  preLine: css({
    whiteSpace: "pre-line",
  }),
  cardContainer: css({
    width: "100%",
    display: "flex",
  }),
  cardTextContainer: css({
    width: "50%",
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
  }),
  sectionContainer: css({
    marginTop: "var(--space-4)",
  }),
  sectionItemContainer: css({
    display: "flex",
    paddingTop: "var(--space-3)",
  }),
  sectionItem: css({
    paddingRight: "var(--space-2)",
  }),
  disclaimer: css({
    paddingTop: "var(--space-4)",
  }),
};
